"use client";

import type { LessonEndingSoonEvent } from "../types/events";

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";
import { format, formatDuration, isAfter, parseJSON } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { useEcho } from "@laravel/echo-react";

import { extendLesson } from "../api/lessonExtension";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import useUserStore from "@/features/auth/store/useUserStore";
import { currentLanguageLocale } from "@/i18n/getDateFnsLocale";
import { useClassroomStore } from "@/features/classroom/store/useClassroomStore.tsx";

// Define the form schema with Zod
const extensionFormSchema = (t: any) =>
  z
    .object({
      extension_minutes: z
        .number()
        .min(10, { message: t("extensionForm.minExtension") })
        .max(30, { message: t("extensionForm.maxExtension") }),
      send_delay_notification: z.boolean().default(false),
      delay_notification_message: z
        .string()
        .max(500, { message: t("extensionForm.messageMaxLength") })
        .optional(),
    })
    .refine(
      (data) => {
        // If sending notification, message is required
        if (data.send_delay_notification && !data.delay_notification_message?.trim()) {
          return false;
        }

        return true;
      },
      {
        message: t("extensionForm.messageRequired"),
        path: ["delay_notification_message"],
      },
    );

type ExtensionFormValues = z.infer<ReturnType<typeof extensionFormSchema>>;

interface LessonExtensionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  eventData: LessonEndingSoonEvent | null;
  workspaceId: string;
  lessonId: string;
}

export function LessonExtensionDialog({
  isOpen,
  onClose,
  eventData,
  workspaceId,
  lessonId,
}: LessonExtensionDialogProps) {
  const { t } = useTranslation(["classroom", "common"]);
  const { user } = useUserStore();
  const { setExtensionInfo, setLessonEndTime } = useClassroomStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldownTimeLeft, setCooldownTimeLeft] = useState(0);
  const [cooldownInterval, setCooldownInterval] = useState<NodeJS.Timeout | null>(null);

  // Get whisper channel for sending extension events
  const { channel } = useEcho(
    `lesson.${lessonId}`,
    [], // No regular events, only whispers
    () => {},
    [lessonId],
  );

  // Check if form is in cooldown period
  const isInCooldown = cooldownTimeLeft > 0;

  // Start cooldown timer
  const startCooldown = () => {
    const cooldownDuration = 60; // 60 seconds

    setCooldownTimeLeft(cooldownDuration);

    const interval = setInterval(() => {
      setCooldownTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(interval);
          setCooldownInterval(null);

          return 0;
        }

        return prevTime - 1;
      });
    }, 1000);

    setCooldownInterval(interval);
  };

  // Clear cooldown on component unmount
  useEffect(() => {
    return () => {
      if (cooldownInterval) {
        clearInterval(cooldownInterval);
      }
    };
  }, [cooldownInterval]);

  // Format cooldown time display
  const formatCooldownTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    }

    return remainingSeconds.toString();
  };

  // Check if there's a next lesson conflict
  const hasNextLessonConflict = (minutes: number) => {
    if (!eventData?.next_lesson || !eventData.lesson_end_time) return false;

    try {
      const currentEndTime = parseJSON(eventData.lesson_end_time);
      const nextLessonStartTime = parseJSON(eventData.next_lesson.start_time);

      // Add the selected extension minutes to current end time and check if it's after next lesson start
      const extendedEndTime = new Date(currentEndTime.getTime() + minutes * 60 * 1000);

      return isAfter(extendedEndTime, nextLessonStartTime);
    } catch (error) {
      console.error("Error checking lesson conflict:", error);

      return false;
    }
  };

  // Initialize the form
  const form = useForm<ExtensionFormValues>({
    resolver: zodResolver(extensionFormSchema(t)),
    defaultValues: {
      extension_minutes: 15,
      send_delay_notification: false,
      delay_notification_message: "",
    },
  });

  // Watch for form value changes
  const extensionMinutes = form.watch("extension_minutes");
  const sendNotification = form.watch("send_delay_notification");

  // Dynamically check for conflicts based on current extension minutes
  const showNotificationFields = hasNextLessonConflict(extensionMinutes);

  // Format duration with proper localization
  const formatExtensionDuration = (minutes: number) => {
    return formatDuration(
      { minutes },
      {
        locale: currentLanguageLocale(),
        delimiter: " ",
      },
    );
  };

  // Format next lesson time with user's timezone
  const formatNextLessonTime = (timeString: string) => {
    try {
      const date = toZonedTime(timeString, user?.timezone ?? "UTC");

      // Format time with user's timezone if available, otherwise use local time
      return format(date, "HH:mm", { locale: currentLanguageLocale() });
    } catch (error) {
      console.error("Error formatting next lesson time:", error);

      return new Date(timeString).toLocaleTimeString();
    }
  };

  // Reset message when notification is unchecked
  if (!sendNotification && form.getValues("delay_notification_message")) {
    form.setValue("delay_notification_message", "");
  }

  // Handle form submission
  const onSubmit = async (data: ExtensionFormValues) => {
    try {
      setIsSubmitting(true);

      const requestData = {
        extension_minutes: data.extension_minutes,
        ...(showNotificationFields && {
          send_delay_notification: data.send_delay_notification,
          ...(data.send_delay_notification && {
            delay_notification_message: data.delay_notification_message,
          }),
        }),
      };

      const response = await extendLesson(workspaceId, lessonId, requestData);

      // Update local lesson end time
      setLessonEndTime(response.data.new_end_time);

      // Send whisper event to notify other participants about the new end time
      if (channel()) {
        channel().whisper("lessonExtended", {
          newEndTime: response.data.new_end_time,
          extensionMinutes: response.data.extension_minutes,
          extendedBy: response.data.extended_by,
          timestamp: Date.now(),
        });
      }

      toast.success(t("extensionForm.success"), {
        description: t("extensionForm.successDescription", {
          minutes: data.extension_minutes,
        }),
      });
      setExtensionInfo(null);

      onClose();
    } catch (error: any) {
      console.error("Failed to extend lesson:", error);
      if (channel()) {
        channel().whisper("lessonExtended", {
          newEndTime: "2025-07-14T16:15:00.000000Z",
          extensionMinutes: 15,
          extendedBy: { id: "01jqcmftghrszehrp9hdgwx9jf", name: null, email: "tutor@ximly.app" },
          timestamp: Date.now(),
        });
      }

      // Handle different types of errors with translated messages
      let errorMessage = t("extensionForm.error");
      let errorDescription = t("extensionForm.errorDescription");

      // Define error key base here to avoid scope issues
      const getErrorKey = (key: string) => `extensionForm.errors.${key}` as any;

      if (error.response) {
        const { status, data: errorData } = error.response;
        const errorCode = errorData?.code;

        // Handle specific error codes with translated messages
        switch (errorCode) {
          case "lesson_workspace_mismatch":
            errorDescription = t(getErrorKey("lessonWorkspaceMismatch"));
            break;
          case "unauthorized_tutor":
            errorDescription = t(getErrorKey("unauthorizedTutor"));
            break;
          case "not_lesson_tutor":
            errorDescription = t(getErrorKey("notLessonTutor"));
            break;
          case "lesson_already_extended":
            errorDescription = t(getErrorKey("lessonAlreadyExtended"));
            break;
          case "lesson_not_in_progress":
            errorDescription = t(getErrorKey("lessonNotInProgress"));
            break;
          case "lesson_not_platform_hosted":
            errorDescription = t(getErrorKey("lessonNotPlatformHosted"));
            break;
          case "no_active_video_room":
            errorDescription = t(getErrorKey("noActiveVideoRoom"));
            break;
          case "lesson_already_too_long":
            errorDescription = t(getErrorKey("lessonAlreadyTooLong"));
            break;
          case "extension_exceeds_limit":
            errorDescription = t(getErrorKey("extensionExceedsLimit"));
            break;
          case "internal_server_error":
            errorDescription = t(getErrorKey("internalServerError"));
            break;
          default:
            // Handle by status code if no specific error code
            switch (status) {
              case 400:
                errorDescription = t(getErrorKey("validationError"));
                break;
              case 403:
                errorDescription = t(getErrorKey("unauthorizedTutor"));
                break;
              case 422:
                // Handle validation errors - these might have field-specific errors
                if (errorData?.errors) {
                  // If there are field validation errors, show the generic validation message
                  errorDescription = t(getErrorKey("validationError"));
                } else {
                  // Fall back to generic validation error for 422 without specific code
                  errorDescription = t(getErrorKey("validationError"));
                }
                break;
              case 500:
                errorDescription = t(getErrorKey("internalServerError"));
                break;
              default:
                errorDescription = t("extensionForm.errorDescription");
            }
        }
      } else if (error.request) {
        // Network error
        errorDescription = t(getErrorKey("networkError"));
      } else {
        // Unknown error
        errorDescription = t("extensionForm.errorDescription");
      }

      toast.error(errorMessage, {
        description: errorDescription,
      });

      startCooldown();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  if (!eventData) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-side-accent/80 text-accent-foreground max-h-[90dvh] max-w-2xl overflow-hidden rounded-2xl border-none p-0 shadow-2xl backdrop-blur-xl max-sm:h-[100dvh] max-sm:max-h-dvh max-sm:rounded-none">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="from-accent-foreground/40 to-accent-foreground/60 absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br" />
          <div className="from-accent-foreground/30 to-accent-foreground/50 absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr" />
        </div>

        <ScrollArea className="relative max-h-[90dvh] max-sm:max-h-[100dvh]">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="p-6"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Conflict warning */}
            {showNotificationFields && eventData.next_lesson && (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 rounded-xl border border-amber-500/20 bg-amber-500/10 p-4 backdrop-blur-sm"
                initial={{ opacity: 0, y: 10 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex items-start gap-3">
                  <Icon
                    className="mt-0.5 flex-shrink-0 text-amber-500"
                    height="1.25rem"
                    icon="lucide:alert-triangle"
                    width="1.25rem"
                  />
                  <div>
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                      {t("extensionForm.conflictWarning")}
                    </p>
                    <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
                      {t("extensionForm.nextLessonAt", {
                        time: formatNextLessonTime(eventData.next_lesson.start_time),
                        subject: eventData.next_lesson.subject_name || t("common:lesson"),
                      })}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            <Form {...form}>
              <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
                {/* Extension Duration Section */}
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-side-accent/50 border-border/50 rounded-xl border p-5 backdrop-blur-sm"
                  initial={{ opacity: 0, y: 10 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="mb-4 flex items-center gap-3">
                    <div className="bg-accent-foreground/10 flex h-8 w-8 items-center justify-center rounded-lg">
                      <Icon
                        className="text-accent-foreground"
                        height="1rem"
                        icon="lucide:timer"
                        width="1rem"
                      />
                    </div>
                    <h3 className="text-accent-foreground text-sm font-semibold">
                      {t("extensionForm.durationSection")}
                    </h3>
                  </div>

                  <FormField
                    control={form.control}
                    name="extension_minutes"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="space-y-4">
                            <div className="text-center">
                              <span className="text-accent-foreground text-2xl font-bold">
                                {formatExtensionDuration(field.value)}
                              </span>
                            </div>
                            <Slider
                              className="py-6"
                              max={30}
                              min={10}
                              sliderClassName="bg-accent-foreground/75"
                              step={1}
                              thumbClassName="bg-accent-foreground ring-accent-foreground/50"
                              value={[field.value]}
                              onValueChange={(values) => field.onChange(values[0])}
                            />
                            <div className="flex justify-between">
                              <span className="text-muted-foreground text-xs">
                                {formatExtensionDuration(10)}
                              </span>
                              <span className="text-muted-foreground text-xs">
                                {formatExtensionDuration(30)}
                              </span>
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </motion.div>

                {/* Notification Section */}
                {showNotificationFields && (
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-side-accent/50 border-border/50 space-y-4 rounded-xl border p-5 backdrop-blur-sm"
                    initial={{ opacity: 0, y: 10 }}
                    transition={{ delay: 0.4 }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-accent-foreground/10 flex h-8 w-8 items-center justify-center rounded-lg">
                        <Icon
                          className="text-accent-foreground"
                          height="1rem"
                          icon="lucide:bell"
                          width="1rem"
                        />
                      </div>
                      <h3 className="text-accent-foreground text-sm font-semibold">
                        {t("extensionForm.notificationSection")}
                      </h3>
                    </div>

                    <FormField
                      control={form.control}
                      name="send_delay_notification"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center space-y-0 space-x-3">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              className={"border-accent-foreground"}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-accent-foreground">
                              {t("extensionForm.sendNotification")}
                            </FormLabel>
                          </div>
                        </FormItem>
                      )}
                    />

                    {sendNotification && (
                      <motion.div
                        animate={{ opacity: 1, height: "auto" }}
                        initial={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <FormField
                          control={form.control}
                          name="delay_notification_message"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                                <Icon
                                  height="0.875rem"
                                  icon="lucide:message-square"
                                  width="0.875rem"
                                />
                                {t("extensionForm.notificationMessage")}
                              </FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <ScrollArea className="border-input bg-background ring-offset-background focus-within:ring-ring h-24 w-full rounded-lg border focus-within:ring-1">
                                    <Textarea
                                      className="[field-sizing:content] min-h-[88px] resize-none rounded-lg border-none p-3 text-sm break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                                      placeholder={t("extensionForm.messagePlaceholder")}
                                      {...field}
                                    />
                                  </ScrollArea>
                                  <div className="text-muted-foreground/60 absolute right-3 bottom-2 text-xs">
                                    {field.value?.length || 0}/500
                                  </div>
                                </div>
                              </FormControl>
                              <FormDescription className="text-muted-foreground/80">
                                {t("extensionForm.messageNote")}
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </motion.div>
                    )}
                  </motion.div>
                )}

                <DialogFooter className="flex gap-3">
                  <Button
                    className="bg-muted/50 hover:bg-muted/30 border-border/50 rounded-xl"
                    disabled={isSubmitting || isInCooldown}
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                  >
                    {t("common:cancel")}
                  </Button>
                  <Button
                    className="bg-accent-foreground text-primary hover:bg-accent-foreground/80 rounded-xl"
                    disabled={isSubmitting || isInCooldown}
                    type="submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Icon
                          className="mr-2 animate-spin"
                          height="1rem"
                          icon="lucide:loader-2"
                          width="1rem"
                        />
                        {t("common:processing")}
                      </>
                    ) : isInCooldown ? (
                      <>
                        <Icon className="mr-2" height="1rem" icon="lucide:clock" width="1rem" />
                        {t("common:wait")} ({formatCooldownTime(cooldownTimeLeft)})
                      </>
                    ) : (
                      <>
                        <Icon className="mr-2" height="1rem" icon="lucide:clock" width="1rem" />
                        {t("extensionForm.extendLesson")} (
                        {formatExtensionDuration(extensionMinutes)})
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </motion.div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
